import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getSharedSupabaseAuthClient } from '../lib/supabaseAuthClient';
import { logger } from '../utils/logger';
import { createApiError, getErrorRequestId } from '../utils/errors';
import { assertCurrentAccountSession, getAccountSession, isCurrentAccountSession, setAccountSession, subscribeAccountSession, type AccountSession } from '../utils/accountSession';

// Error response type from the backend
interface APIErrorResponse {
    detail: string;
    code?: string;
    errors?: Record<string, string[]>;
}

// Vercel's rewrite rules which proxy `/api/*` to the Python backend.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const TOKEN_EXPIRY_BUFFER_MS = 30_000;

// Create Axios instance with default config
export const api = axios.create({
    baseURL: API_BASE_URL,
    // Timeout in milliseconds (5 minutes to match Vercel function limit)
    timeout: 300000,
});

// Token caching to avoid network round-trips per request
let cachedAccessToken: string | null = null;
let tokenExpiresAt: number = 0;
let refreshTokenPromise: Promise<string | null> | null = null;

// Store the interceptor IDs so they can be ejected if setup is called again
let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

interface RetryableAxiosConfig extends InternalAxiosRequestConfig {
    _retryWithFreshToken?: boolean;
    _accountSession?: AccountSession;
}

function clearCachedAccessToken(): void {
    cachedAccessToken = null;
    tokenExpiresAt = 0;
}

function getTokenExpiry(token: string): number {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
    } catch {
        return 0;
    }
}

function cacheAccessToken(token: string): void {
    cachedAccessToken = token;
    tokenExpiresAt = getTokenExpiry(token);
}

function cacheSessionAccessToken(session: { access_token?: string } | null | undefined): void {
    if (session?.access_token) {
        cacheAccessToken(session.access_token);
        return;
    }
    clearCachedAccessToken();
}

function hasUsableAccessToken(token: string | null, expiresAt: number): token is string {
    return Boolean(token && expiresAt > Date.now() + TOKEN_EXPIRY_BUFFER_MS);
}

function requestHasAuthorizationHeader(config?: InternalAxiosRequestConfig): boolean {
    if (!config?.headers) {
        return false;
    }

    const headers = config.headers as Record<string, unknown>;
    return typeof headers.Authorization === 'string' || typeof headers.authorization === 'string';
}

async function refreshAccessToken(reason: 'request' | 'response'): Promise<string | null> {
    const account = getAccountSession();
    if (refreshTokenPromise) {
        logger.log(`[Axios] Awaiting in-flight token refresh (${reason})...`);
        return refreshTokenPromise;
    }

    refreshTokenPromise = (async () => {
        logger.log(`[Axios] Refreshing Supabase session before ${reason} handling...`);
        try {
            const { data: { session }, error } = await supabaseAuth.refreshSession();
            assertCurrentAccountSession(account);
            if (error) {
                logger.warn('[Axios] Token refresh returned an error:', error);
                clearCachedAccessToken();
                return null;
            }

            if (!session?.access_token) {
                logger.warn('[Axios] Token refresh completed without a session');
                clearCachedAccessToken();
                return null;
            }

            cacheAccessToken(session.access_token);
            logger.log('[Axios] Token refreshed successfully');
            return session.access_token;
        } catch (error) {
            assertCurrentAccountSession(account);
            logger.warn('[Axios] Token refresh failed:', error);
            clearCachedAccessToken();
            return null;
        } finally {
            if (isCurrentAccountSession(account)) refreshTokenPromise = null;
        }
    })();

    return refreshTokenPromise;
}

async function getValidAccessToken(): Promise<string | null> {
    const account = getAccountSession();
    if (hasUsableAccessToken(cachedAccessToken, tokenExpiresAt)) {
        return cachedAccessToken;
    }

    logger.log('[Axios] Token expired or missing, fetching current session...');
    try {
        const { data: { session } } = await supabaseAuth.getSession();
        assertCurrentAccountSession(account);
        if (!session?.access_token) {
            logger.warn('[Axios] No session available');
            clearCachedAccessToken();
            return null;
        }

        cacheAccessToken(session.access_token);
        if (!hasUsableAccessToken(session.access_token, tokenExpiresAt)) {
            logger.log('[Axios] Session token is expired or near expiry, refreshing before request...');
            return refreshAccessToken('request');
        }

        logger.log('[Axios] Loaded valid token from current session');
        return session.access_token;
    } catch (error) {
        assertCurrentAccountSession(account);
        logger.error('[Axios] Failed to get Supabase session:', error);
        clearCachedAccessToken();
        return null;
    }
}

// Get the shared Supabase client and listen for auth state changes to cache the token
const supabaseAuth = getSharedSupabaseAuthClient();
// The provider may receive the auth event first; reset on the identity change
// itself rather than depending on the order of Supabase listeners.
subscribeAccountSession(() => {
    clearCachedAccessToken();
    refreshTokenPromise = null;
});
supabaseAuth.onAuthStateChange((_event, session) => {
    setAccountSession(session?.user?.id ?? null);
    cacheSessionAccessToken(session);
});

export const setupSupabaseAxiosInterceptors = () => {
    // If old interceptors exist, eject them to prevent duplicates
    if (requestInterceptorId !== null) {
        api.interceptors.request.eject(requestInterceptorId);
    }
    if (responseInterceptorId !== null) {
        api.interceptors.response.eject(responseInterceptorId);
    }

    requestInterceptorId = api.interceptors.request.use(
        async (config: RetryableAxiosConfig) => {
            config._accountSession ??= getAccountSession();
            assertCurrentAccountSession(config._accountSession);
            // Ensure baseURL is set correctly for each request
            config.baseURL = API_BASE_URL;

            const token = await getValidAccessToken();
            assertCurrentAccountSession(config._accountSession);

            if (token) {
                const headers = config.headers as Record<string, unknown>;
                headers.Authorization = `Bearer ${token}`;
            } else {
                logger.warn('[Axios] Making request without auth token to:', config.url);
            }

            if (config.data instanceof FormData && config.headers) {
                // Ensure multipart requests aren't forced to application/json
                const headers = config.headers as Record<string, unknown> & { setContentType?: (value?: unknown) => void };
                if (typeof headers.setContentType === 'function') {
                    headers.setContentType(undefined); // let the browser set the boundary
                } else {
                    delete headers['Content-Type'];
                    delete headers['content-type'];
                }
            }

            return config;
        },
        (error: AxiosError) => {
            return Promise.reject(error);
        }
    );

    // Add response interceptor for token refresh on 401 and general error handling
    responseInterceptorId = api.interceptors.response.use(
        (response: AxiosResponse) => {
            const account = (response.config as RetryableAxiosConfig)?._accountSession;
            if (account) assertCurrentAccountSession(account);
            return response;
        },
        async (error: AxiosError<APIErrorResponse>) => {
            // Handle 401 with token refresh
            const retryConfig = error.config as RetryableAxiosConfig | undefined;
            if (retryConfig?._accountSession) assertCurrentAccountSession(retryConfig._accountSession);
            if (
                error.response?.status === 401
                && retryConfig
                && !retryConfig._retryWithFreshToken
                && (cachedAccessToken || requestHasAuthorizationHeader(retryConfig))
            ) {
                const refreshedToken = await refreshAccessToken('response');
                if (retryConfig._accountSession) assertCurrentAccountSession(retryConfig._accountSession);
                if (refreshedToken) {
                    retryConfig._retryWithFreshToken = true;
                    const headers = retryConfig.headers as Record<string, unknown>;
                    headers.Authorization = `Bearer ${refreshedToken}`;
                    return api.request(retryConfig);
                }

                window.location.href = '/sign-in';
            }

            // Handle other errors
            if (error.response) {
                const requestId = getErrorRequestId(error);
                if (requestId) {
                    (error as AxiosError<APIErrorResponse> & { requestId?: string }).requestId = requestId;
                }
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                
                // Handle specific status codes
                switch (error.response.status) {
                    case 401:
                        // Already handled above, but redirect if refresh failed
                        window.location.href = '/sign-in';
                        break;
                    case 403:
                        // Handle forbidden
                        break;
                    case 404:
                        // Handle not found
                        break;
                    case 422:
                        // Handle validation errors
                        break;
                    default:
                        // Handle other errors
                        break;
                }

                // Preserve the original error structure so components can access error.response.data.detail
                // This is important for specific error handling like SAFETY_FILTER_ERROR
                return Promise.reject(error);
            } else if (error.request) {
                // The request was made but no response was received
                return Promise.reject(createApiError(
                    'No response received from server. Please check your connection.',
                    { cause: error },
                ));
            } else {
                // Something happened in setting up the request that triggered an Error
                return Promise.reject(createApiError(
                    'Error setting up the request. Please try again.',
                    { cause: error },
                ));
            }
        }
    );
};

// Legacy function for backward compatibility during migration
export const setupAxiosInterceptors = () => {
    logger.warn('setupAxiosInterceptors is deprecated. Use setupSupabaseAxiosInterceptors instead.');
    setupSupabaseAxiosInterceptors();
};

// Initialize Supabase interceptors immediately
setupSupabaseAxiosInterceptors(); 
